import type { z } from 'zod'

import { useAuthStore } from '@/stores/auth-store'

import {
  ApiError,
  buildHeaders,
  buildUrl,
  type HttpMethod,
  readBody,
  toApiError,
  toNetworkError,
} from './http'
import { obterAccessToken, renovarAccessToken } from './sessao'

/**
 * Cliente HTTP autenticado do painel.
 *
 * Toda falha — de rede, de contrato ou de negócio — sai daqui como `ApiError`,
 * então a camada de UI nunca precisa inspecionar `Response` na mão.
 *
 * ---------------------------------------------------------------------------
 * O INTERCEPTOR
 * ---------------------------------------------------------------------------
 * Duas coisas acontecem em volta de cada requisição:
 *
 *   1. ANTES: se o access token está vencido (ou a menos de 30s de vencer),
 *      `obterAccessToken()` renova. Deduplicado — ver `@/lib/sessao`.
 *   2. DEPOIS: um 401 dispara UMA renovação e UMA repetição da requisição. Se a
 *      renovação falhar, o erro sobe e a sessão já foi limpa; o guard de rota
 *      manda para /login na navegação seguinte.
 *
 * A repetição acontece no máximo uma vez por chamada. Repetir em laço com
 * credencial inválida é como se transforma um token expirado num bloqueio de
 * conta por excesso de tentativas.
 */

export { ApiError }
export type { HttpMethod }
export { apiErrorEnvelopeSchema, CLIENT_ERROR_CODES, errorMessage } from './http'
export type { ApiErrorEnvelope, ApiErrorInit } from './http'

export interface ApiCallOptions {
  readonly signal?: AbortSignal | undefined
  readonly headers?: Readonly<Record<string, string>> | undefined
  /** Invariante nº 9: obrigatório em criação de reserva e em pagamento. */
  readonly idempotencyKey?: string | undefined
  /**
   * Rota pública (login, refresh, redefinição de senha): não manda Bearer e não
   * tenta renovar no 401 — em `/auth/login` o 401 significa "senha errada", e
   * renovar ali seria pedir token novo para quem nem entrou.
   */
  readonly semAutenticacao?: boolean | undefined
}

export interface ApiRequestOptions<TSchema extends z.ZodType> extends ApiCallOptions {
  /** Schema Zod da resposta — é ele que dá o tipo de retorno da chamada. */
  readonly schema: TSchema
  readonly method?: HttpMethod | undefined
  readonly body?: unknown
}

interface PedidoHttp {
  readonly method: HttpMethod
  readonly body: unknown
  readonly temCorpo: boolean
  readonly headers: Readonly<Record<string, string>> | undefined
  readonly idempotencyKey: string | undefined
  readonly signal: AbortSignal | undefined
}

async function executar(
  path: string,
  pedido: PedidoHttp,
  accessToken: string | null,
): Promise<Response> {
  const init: RequestInit = {
    method: pedido.method,
    headers: buildHeaders({
      hasBody: pedido.temCorpo,
      headers: pedido.headers,
      idempotencyKey: pedido.idempotencyKey,
      accessToken,
    }),
    // O cookie httpOnly de refresh precisa acompanhar a requisição.
    credentials: 'include',
    ...(pedido.temCorpo ? { body: JSON.stringify(pedido.body) } : {}),
    ...(pedido.signal === undefined ? {} : { signal: pedido.signal }),
  }

  try {
    return await fetch(buildUrl(path), init)
  } catch (cause) {
    // Cancelamento não é falha: o TanStack Query precisa vê-lo como AbortError.
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause
    }

    throw toNetworkError(cause)
  }
}

export async function apiRequest<TSchema extends z.ZodType>(
  path: string,
  options: ApiRequestOptions<TSchema>,
): Promise<z.output<TSchema>> {
  const semAutenticacao = options.semAutenticacao ?? false

  const pedido: PedidoHttp = {
    method: options.method ?? 'GET',
    body: options.body,
    temCorpo: options.body !== undefined,
    headers: options.headers,
    idempotencyKey: options.idempotencyKey,
    signal: options.signal,
  }

  const token = semAutenticacao ? null : await obterAccessToken()
  let response = await executar(path, pedido, token)

  if (response.status === 401 && !semAutenticacao && useAuthStore.getState().status !== 'anonima') {
    const novoToken = await renovarAccessToken()

    if (novoToken !== null) {
      response = await executar(path, pedido, novoToken)
    }
  }

  const payload = await readBody(response)

  if (!response.ok) {
    throw toApiError(response.status, payload)
  }

  const parsed = options.schema.safeParse(payload)
  if (!parsed.success) {
    throw new ApiError({
      status: response.status,
      code: 'INVALID_RESPONSE',
      message: 'O servidor respondeu em um formato que o painel não reconhece.',
      details: parsed.error.issues,
      traceId: response.headers.get('x-trace-id') ?? undefined,
    })
  }

  return parsed.data
}

export function apiGet<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  options: ApiCallOptions = {},
): Promise<z.output<TSchema>> {
  return apiRequest(path, { ...options, schema, method: 'GET' })
}

export function apiPost<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  options: ApiCallOptions = {},
): Promise<z.output<TSchema>> {
  return apiRequest(path, { ...options, schema, method: 'POST', body })
}

export function apiPatch<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  options: ApiCallOptions = {},
): Promise<z.output<TSchema>> {
  return apiRequest(path, { ...options, schema, method: 'PATCH', body })
}

/**
 * `PUT` — substituição INTEGRAL do recurso.
 *
 * Distinto de `apiPatch` de propósito, e não por simetria de tabela: a única
 * rota que o usa hoje (`PUT /rbac/roles/:codigo/permissions`) troca a lista
 * inteira de concessões do papel. Mandá-la por `PATCH` sugeriria que o corpo é
 * um delta — e um delta interpretado como substituição é como se apaga uma
 * permissão sem ninguém pedir.
 */
export function apiPut<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  options: ApiCallOptions = {},
): Promise<z.output<TSchema>> {
  return apiRequest(path, { ...options, schema, method: 'PUT', body })
}

export function apiDelete<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  options: ApiCallOptions = {},
): Promise<z.output<TSchema>> {
  return apiRequest(path, { ...options, schema, method: 'DELETE' })
}

/**
 * Baixa um arquivo binário da API e o entrega ao navegador.
 *
 * Existe separado de `apiGet` porque tudo o que aquele faz depois da resposta —
 * ler como JSON, validar com Zod — está errado aqui: um XLSX não é JSON e não
 * tem schema. O que ELE faz antes, e que aqui importa, é o token e a renovação
 * automática do access token expirado; por isso este caminho reusa `executar`
 * em vez de chamar `fetch` direto.
 *
 * ⚠️ O NOME DO ARQUIVO VEM DO SERVIDOR, do `Content-Disposition`. Montá-lo aqui
 * significaria a mesma exportação chegar com nomes diferentes conforme a tela
 * que a pediu — e o nome carrega o período, que é o que distingue duas
 * planilhas na pasta de downloads.
 */
export async function apiDownload(path: string, nomeDeReserva: string): Promise<void> {
  const token = await obterAccessToken()
  const pedido: PedidoHttp = {
    method: 'GET',
    body: undefined,
    temCorpo: false,
    headers: undefined,
    idempotencyKey: undefined,
    signal: undefined,
  }

  let response = await executar(path, pedido, token)
  if (response.status === 401 && useAuthStore.getState().status !== 'anonima') {
    const novoToken = await renovarAccessToken()
    if (novoToken !== null) {
      response = await executar(path, pedido, novoToken)
    }
  }

  if (!response.ok) {
    // O corpo do erro AINDA é JSON — o servidor só manda binário no caminho
    // feliz. Ler como JSON aqui é o que faz um 403 virar mensagem legível em
    // vez de "falha ao baixar".
    throw toApiError(response.status, await readBody(response))
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  try {
    const ancora = document.createElement('a')
    ancora.href = url
    ancora.download = nomeDoContentDisposition(response) ?? nomeDeReserva
    document.body.appendChild(ancora)
    ancora.click()
    ancora.remove()
  } finally {
    // Sem o `revoke`, cada exportação deixa o arquivo inteiro na memória da aba
    // até o recarregamento — e uma planilha de frota não é pequena.
    URL.revokeObjectURL(url)
  }
}

function nomeDoContentDisposition(response: Response): string | null {
  const cabecalho = response.headers.get('content-disposition')
  if (cabecalho === null) return null
  const casamento = /filename="([^"]+)"/.exec(cabecalho)
  return casamento?.[1] ?? null
}
